import { useRef, useEffect, useState } from 'react';
import { ParticleNetwork } from '@/components/effects/ParticleNetwork';
import { GlitchText } from '@/components/effects/GlitchText';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';

// Backend URL resolution — same logic as the rest of the app (empty = same-origin).
const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const FloatingCard = ({ children, delay = 0, className = '' }) => (
  <div
    className={`hidden lg:block absolute ${className}`}
    style={{
      background: 'rgba(10,10,18,0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(139,92,246,0.2)',
      borderRadius: '10px',
      padding: '14px 18px',
      animation: `floatY ${6 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
    }}
  >
    {children}
  </div>
);

export const HeroSection = () => {
  const [statsRef, statsInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [cvesMonitored, setCvesMonitored] = useState(30000);
  const [criticalCves, setCriticalCves] = useState(250);

  // Live threat-intel from NVD — shares the sessionStorage cache key with
  // LiveThreatIntelligence so it hydrates instantly if that section loaded first.
  useEffect(() => {
    let cancelled = false;
    try {
      const cached = JSON.parse(sessionStorage.getItem('acodia_threat_intel_cache'));
      if (cached && Date.now() - cached.ts < 3600 * 1000 && cached.payload) {
        const p = cached.payload;
        if (typeof p.cves_this_year === 'number' && p.cves_this_year > 0) setCvesMonitored(p.cves_this_year);
        if (typeof p.critical_cves_this_month === 'number' && p.critical_cves_this_month > 0) setCriticalCves(p.critical_cves_this_month);
      }
    } catch (e) { /* ignore parse/quota errors */ }

    fetch(`${BACKEND_URL}/api/public/threat-intel`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        if (cancelled) return;
        if (typeof payload.cves_this_year === 'number' && payload.cves_this_year > 0) setCvesMonitored(payload.cves_this_year);
        if (typeof payload.critical_cves_this_month === 'number' && payload.critical_cves_this_month > 0) setCriticalCves(payload.critical_cves_this_month);
        try {
          sessionStorage.setItem('acodia_threat_intel_cache', JSON.stringify({ ts: Date.now(), payload }));
        } catch (e) { /* ignore quota errors */ }
      })
      .catch((err) => { console.warn('Hero threat intel fetch failed:', err); });

    return () => { cancelled = true; };
  }, []);

  const stats = [
    { value: cvesMonitored, suffix: '+', label: 'CVEs Monitored' },
    { value: '24/7', label: 'Continuous Monitoring' },
    { value: '7', suffix: ' days', label: 'Free Trial' },
    { price: '£49', label: 'Acodia Watch from', href: '/auth', accent: true },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
      style={{ background: 'var(--bg)' }}
    >
      {/* Hero Mesh Gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'var(--hero-mesh)' }}
      />

      {/* Particle Network */}
      <div className="absolute inset-0">
        <ParticleNetwork />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 w-full py-20 lg:py-32">
        <div className="max-w-3xl">
          {/* Kicker */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8"
            style={{
              background: 'rgba(139,92,246,0.08)',
              border: '1px solid rgba(139,92,246,0.20)',
            }}
            data-testid="hero-kicker"
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: 'var(--ok)', animation: 'pulseDot 2s ease-in-out infinite' }}
            />
            <span className="font-body text-xs tracking-widest uppercase" style={{ color: 'var(--a)' }}>
              Cyber Defence · Career Growth · Digital Marketing
            </span>
          </div>

          {/* Headline */}
          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold leading-[0.9] tracking-[-0.04em] mb-6"
            style={{ fontSize: 'clamp(3rem, 8.5vw, 8rem)', color: 'var(--white)' }}
          >
            Grow. Defend.<br />
            Be <GlitchText text="Secure." />
          </h1>

          {/* Subheadline */}
          <p
            data-testid="hero-subheadline"
            className="font-body font-light text-base sm:text-lg max-w-xl mb-10"
            style={{ color: 'var(--tm)', lineHeight: '1.72' }}
          >
            From Acodia Watch security monitoring to expert-led career training and digital growth strategy, Acodia delivers specialist solutions built for London's most ambitious businesses with the rigour of a global firm, delivered without the layers.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4" data-testid="hero-ctas">
            <a href="#scanner">
              <Button
                data-testid="hero-primary-cta-button"
                className="btn-shimmer font-body font-medium text-sm px-7 py-3.5 rounded-[3px] transition-all duration-200"
                style={{
                  background: 'var(--a)',
                  color: '#fff',
                  boxShadow: '0 14px 40px rgba(139,92,246,0.35)',
                  height: 'auto',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--a-bright)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--a)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Free Security Scan
                <ArrowRight size={16} className="ml-1" />
              </Button>
            </a>
            <a href="#services">
              <Button
                data-testid="hero-secondary-cta-button"
                className="font-body font-medium text-sm px-7 py-3.5 rounded-[3px] transition-all duration-200"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(139,92,246,0.25)',
                  color: 'var(--white)',
                  height: 'auto',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139,92,246,0.25)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Explore Services
              </Button>
            </a>
          </div>
        </div>

        {/* Floating Status Cards */}
        <FloatingCard className="right-[5vw] top-[30%]" delay={0}>
          <div className="flex items-center gap-3">
            <Shield size={16} style={{ color: 'var(--ok)' }} />
            <div>
              <p className="font-mono text-xs" style={{ color: 'var(--ok)' }}>CRITICAL CVES THIS MONTH</p>
              <p className="font-display text-lg font-bold" style={{ color: 'var(--white)' }}>
                {criticalCves.toLocaleString()}
              </p>
            </div>
          </div>
        </FloatingCard>

        <FloatingCard className="right-[8vw] top-[55%]" delay={3}>
          <div className="flex items-center gap-3">
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: 'var(--a)', animation: 'pulseDot 2s ease-in-out infinite' }}
            />
            <div>
              <p className="font-mono text-xs" style={{ color: 'var(--tm)' }}>ACTIVE TRAINING TRACK</p>
              <p className="font-body text-sm font-medium" style={{ color: 'var(--white)' }}>Expert-Led Sessions</p>
            </div>
          </div>
        </FloatingCard>

        {/* Stats Row */}
        <div
          ref={statsRef}
          data-testid="hero-stats-row"
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 mt-16 lg:mt-24 pt-8 border-t"
          style={{ borderColor: 'var(--b1)' }}
        >
          {stats.map((stat) => {
            const content = (
              <>
                <p
                  className="font-display font-extrabold whitespace-nowrap"
                  style={{
                    color: stat.accent ? 'var(--a)' : 'var(--white)',
                    fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
                  }}
                >
                  {stat.price ? (
                    stat.price
                  ) : (
                    <>
                      {stat.prefix || ''}
                      {typeof stat.value === 'number' ? (
                        statsInView ? (
                          <CountUp
                            end={stat.value}
                            duration={2.2}
                            decimals={stat.decimals || 0}
                            separator=","
                          />
                        ) : '0'
                      ) : (
                        stat.value
                      )}
                      {stat.suffix ? <span style={{ color: 'var(--a)' }}>{stat.suffix}</span> : null}
                    </>
                  )}
                </p>
                <p className="font-mono text-xs mt-1" style={{ color: 'var(--ts)' }}>{stat.label}</p>
              </>
            );

            if (stat.href) {
              return (
                <a
                  key={stat.label}
                  href={stat.href}
                  data-testid="hero-stat-active-guard"
                  className="text-center lg:text-left block transition-opacity duration-200 hover:opacity-90"
                >
                  {content}
                </a>
              );
            }

            return (
              <div key={stat.label} className="text-center lg:text-left">
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
